import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { errMsg } from "@/lib/api";
import { RINKS, RINK_LABEL, RINK_BADGE, AID_PRICE, euro } from "@/lib/rinks";

const EMPTY = {
  rink: "sligo", group_name: "", contact_name: "", contact_phone: "", contact_email: "",
  date: "", session_time: "", number_attending: 10, price_per_person: 16,
  skating_aids: 0, free_aids: 0, party_room: false, party_room_from: "", party_room_to: "",
  payment_status: "unpaid", allocation_removed: "", additional_info: "", notes: "",
  split_pricing: false, child_count: 0, child_price: 10, adult_count: 0, adult_price: 16,
  company_address: "", vat_rate: 0, po_number: "",
  food_booking: false, food_vendor: "", vendor_notified: false, food_comment: "",
};

const addMins = (t, mins) => {
  if (!t || !t.includes(":")) return "";
  const [h, m] = t.split(":").map(Number);
  const tot = h * 60 + m + mins;
  return `${String(Math.floor(tot / 60) % 24).padStart(2, "0")}:${String(tot % 60).padStart(2, "0")}`;
};

const Section = ({ title, testId }) => (
  <div
    className="md:col-span-2 bg-slate-900 text-white font-mono-tight text-xs uppercase tracking-widest font-bold px-3 py-2 border-2 border-slate-900 shadow-[2px_2px_0px_0px_#0f172a] mt-2"
    data-testid={testId}
  >
    {title}
  </div>
);

export default function BookingDialog({ open, onOpenChange, mode, booking, presetDate, slots, defaultRink, canPickRink, onSaved }) {
  const readOnly = mode === "view";
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (mode === "new") {
      const rink = defaultRink || "sligo";
      setForm({ ...EMPTY, rink, date: presetDate || "", session_time: (slots[rink] || [])[0] || "" });
    } else if (booking) {
      setForm({
        rink: booking.rink, group_name: booking.group_name, contact_name: booking.contact_name,
        contact_phone: booking.contact_phone || "", contact_email: booking.contact_email || "",
        date: booking.date, session_time: booking.session_time,
        number_attending: booking.number_attending, price_per_person: booking.price_per_person,
        skating_aids: booking.skating_aids ?? 0, free_aids: booking.free_aids ?? 0,
        party_room: !!booking.party_room, party_room_from: booking.party_room_from || "",
        party_room_to: booking.party_room_to || "",
        payment_status: booking.payment_status || "unpaid",
        allocation_removed: booking.allocation_removed || "",
        company_address: booking.company_address || "",
        vat_rate: booking.vat_rate ?? 0,
        po_number: booking.po_number || "",
        food_booking: !!booking.food_booking, food_vendor: booking.food_vendor || "",
        vendor_notified: !!booking.vendor_notified, food_comment: booking.food_comment || "",
        split_pricing: !!booking.split_pricing,
        child_count: booking.child_count ?? 0, child_price: booking.child_price ?? 10,
        adult_count: booking.adult_count ?? 0, adult_price: booking.adult_price ?? 16,
        additional_info: booking.additional_info || "", notes: booking.notes || "",
      });
    }
  }, [open, mode, booking, presetDate, defaultRink, slots]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const childTotal = (Number(form.child_count) || 0) * (Number(form.child_price) || 0);
  const adultTotal = (Number(form.adult_count) || 0) * (Number(form.adult_price) || 0);
  const skatersTotal = form.split_pricing
    ? childTotal + adultTotal
    : (Number(form.number_attending) || 0) * (Number(form.price_per_person) || 0);
  const aidsTotal = (Number(form.skating_aids) || 0) * AID_PRICE;
  const total = skatersTotal + aidsTotal;
  const vatRate = Number(form.vat_rate) || 0;
  const vatAmount = Math.round(total * vatRate) / 100;
  const grandTotal = total + vatAmount;
  const rinkSlots = slots[form.rink] || [];
  const timeOptions = form.session_time && !rinkSlots.includes(form.session_time)
    ? [...rinkSlots, form.session_time].sort()
    : rinkSlots;

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const payload = {
      ...form,
      number_attending: form.split_pricing
        ? (Number(form.child_count) || 0) + (Number(form.adult_count) || 0)
        : Number(form.number_attending),
      price_per_person: Number(form.price_per_person),
      child_count: Number(form.child_count) || 0,
      child_price: Number(form.child_price) || 0,
      adult_count: Number(form.adult_count) || 0,
      adult_price: Number(form.adult_price) || 0,
      skating_aids: Number(form.skating_aids) || 0,
      free_aids: Number(form.free_aids) || 0,
      vat_rate: Number(form.vat_rate) || 0,
    };
    if (!canPickRink) delete payload.rink;
    try {
      if (mode === "new") {
        await api.post("/bookings", payload);
        toast.success("Booking created");
      } else {
        await api.put(`/bookings/${booking.id}`, payload);
        toast.success("Booking updated");
      }
      onSaved();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const title = mode === "new" ? "New booking" : mode === "edit" ? "Edit booking" : "Booking details";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="border-4 border-slate-900 shadow-[8px_8px_0px_0px_#0f172a] rounded-none max-w-2xl max-h-[90vh] overflow-y-auto bg-white"
        data-testid="booking-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase text-slate-900 flex items-center gap-3">
            {title}
            <span className={`badge-brutal ${RINK_BADGE[form.rink]}`} data-testid="booking-dialog-rink">{RINK_LABEL[form.rink]}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">Group booking form for {RINK_LABEL[form.rink]}</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="border-2 border-rose-500 bg-rose-50 text-rose-700 p-3 font-mono-tight text-xs font-bold" data-testid="booking-form-error">
            {error}
          </div>
        )}

        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2" data-testid="booking-form">
          <Section title="Group & contact details" testId="section-group-contact" />
          {canPickRink && !readOnly && (
            <div>
              <label className="label-brutal" htmlFor="bk-rink">Rink</label>
              <select id="bk-rink" className="input-brutal" value={form.rink}
                onChange={(e) => {
                  const rink = e.target.value;
                  setForm((f) => ({ ...f, rink, session_time: (slots[rink] || [])[0] || f.session_time }));
                }}
                data-testid="booking-rink-select">
                {RINKS.map((r) => <option key={r} value={r}>{RINK_LABEL[r]}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="label-brutal" htmlFor="bk-group">Group name *</label>
            <input id="bk-group" className="input-brutal" required disabled={readOnly} value={form.group_name} onChange={set("group_name")} placeholder="e.g. St. Patrick's NS" data-testid="booking-group-name-input" />
          </div>
          <div>
            <label className="label-brutal" htmlFor="bk-contact">Contact name *</label>
            <input id="bk-contact" className="input-brutal" required disabled={readOnly} value={form.contact_name} onChange={set("contact_name")} placeholder="Main contact person" data-testid="booking-contact-name-input" />
          </div>
          <div>
            <label className="label-brutal" htmlFor="bk-phone">Contact phone</label>
            <input id="bk-phone" className="input-brutal" disabled={readOnly} value={form.contact_phone} onChange={set("contact_phone")} placeholder="087 …" data-testid="booking-contact-phone-input" />
          </div>
          <div>
            <label className="label-brutal" htmlFor="bk-email">Contact email</label>
            <input id="bk-email" type="email" className="input-brutal" disabled={readOnly} value={form.contact_email} onChange={set("contact_email")} placeholder="group@example.com" data-testid="booking-contact-email-input" />
          </div>
          <div className="md:col-span-2">
            <label className="label-brutal" htmlFor="bk-company-address">Company address (optional — printed on the invoice)</label>
            <input id="bk-company-address" className="input-brutal" disabled={readOnly} value={form.company_address} onChange={set("company_address")} placeholder="Unit 1, Main Street, Town, Co. …" data-testid="booking-company-address-input" />
          </div>
          <Section title="Date & session" testId="section-date-session" />
          <div>
            <label className="label-brutal" htmlFor="bk-date">Date *</label>
            <input id="bk-date" type="date" className="input-brutal" required disabled={readOnly} value={form.date} onChange={set("date")} data-testid="booking-date-input" />
          </div>
          <div>
            <label className="label-brutal" htmlFor="bk-time">Session time *</label>
            <select id="bk-time" className="input-brutal" required disabled={readOnly} value={form.session_time}
              onChange={(e) => setForm((f) => {
                const session_time = e.target.value;
                if (f.party_room) {
                  return { ...f, session_time, party_room_from: addMins(session_time, 45), party_room_to: addMins(session_time, 90) };
                }
                return { ...f, session_time };
              })}
              data-testid="booking-session-time-select">
              <option value="" disabled>Select a session…</option>
              {timeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-3 cursor-pointer border-2 border-slate-900 bg-white p-3 shadow-[2px_2px_0px_0px_#0f172a] w-fit" data-testid="booking-party-room-label">
              <input
                type="checkbox"
                className="w-5 h-5 accent-sky-500"
                checked={form.party_room}
                disabled={readOnly}
                onChange={(e) => setForm((f) => {
                  const party_room = e.target.checked;
                  if (party_room && f.session_time) {
                    return { ...f, party_room, party_room_from: addMins(f.session_time, 45), party_room_to: addMins(f.session_time, 90) };
                  }
                  return { ...f, party_room };
                })}
                data-testid="booking-party-room-checkbox"
              />
              <span className="font-mono-tight text-xs uppercase tracking-widest font-bold">Group wants the party room</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer border-2 border-slate-900 bg-white p-3 shadow-[2px_2px_0px_0px_#0f172a] w-fit" data-testid="booking-food-booking-label">
              <input
                type="checkbox"
                className="w-5 h-5 accent-sky-500"
                checked={form.food_booking}
                disabled={readOnly}
                onChange={(e) => setForm((f) => ({ ...f, food_booking: e.target.checked }))}
                data-testid="booking-food-booking-checkbox"
              />
              <span className="font-mono-tight text-xs uppercase tracking-widest font-bold">Food booking</span>
            </label>
            </div>
            <p className="font-mono-tight text-[11px] uppercase tracking-widest text-slate-500 mt-2">
              Defaults to the 45 min straight after the skate session
            </p>
            {form.party_room && (
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <label className="label-brutal" htmlFor="bk-pr-from">Party room from</label>
                  <input id="bk-pr-from" type="time" className="input-brutal" disabled={readOnly} value={form.party_room_from} onChange={set("party_room_from")} data-testid="booking-party-room-from" />
                </div>
                <div>
                  <label className="label-brutal" htmlFor="bk-pr-to">Party room to</label>
                  <input id="bk-pr-to" type="time" className="input-brutal" disabled={readOnly} value={form.party_room_to} onChange={set("party_room_to")} data-testid="booking-party-room-to" />
                </div>
              </div>
            )}
            {form.food_booking && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 border-t-2 border-slate-900 pt-4" data-testid="food-booking-fields">
                <div>
                  <label className="label-brutal" htmlFor="bk-vendor">Food vendor name</label>
                  <input id="bk-vendor" className="input-brutal" disabled={readOnly} value={form.food_vendor} onChange={set("food_vendor")} placeholder="Vendor company name" data-testid="booking-food-vendor-input" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-3 cursor-pointer border-2 border-slate-900 bg-white p-3 shadow-[2px_2px_0px_0px_#0f172a] w-fit">
                    <input
                      type="checkbox"
                      className="w-5 h-5 accent-sky-500"
                      checked={form.vendor_notified}
                      disabled={readOnly}
                      onChange={(e) => setForm((f) => ({ ...f, vendor_notified: e.target.checked }))}
                      data-testid="booking-vendor-notified-checkbox"
                    />
                    <span className="font-mono-tight text-xs uppercase tracking-widest font-bold">Vendor notified</span>
                  </label>
                </div>
                <div className="md:col-span-2">
                  <label className="label-brutal" htmlFor="bk-food-comment">Food comment</label>
                  <textarea id="bk-food-comment" rows={2} className="input-brutal resize-y" disabled={readOnly} value={form.food_comment} onChange={set("food_comment")} placeholder="e.g. 25 hot meals at 14:00, 3 vegetarian…" data-testid="booking-food-comment-input" />
                </div>
              </div>
            )}
          </div>
          <Section title="Attendance & pricing" testId="section-attendance-pricing" />
          <div className="md:col-span-2">
            <label className="flex items-center gap-3 cursor-pointer border-2 border-slate-900 bg-white p-3 shadow-[2px_2px_0px_0px_#0f172a] w-fit" data-testid="booking-split-pricing-label">
              <input
                type="checkbox"
                className="w-5 h-5 accent-sky-500"
                checked={form.split_pricing}
                disabled={readOnly}
                onChange={(e) => setForm((f) => ({ ...f, split_pricing: e.target.checked }))}
                data-testid="booking-split-pricing-checkbox"
              />
              <span className="font-mono-tight text-xs uppercase tracking-widest font-bold">Different child &amp; adult rates</span>
            </label>
          </div>
          {form.split_pricing ? (
            <>
              <div>
                <label className="label-brutal" htmlFor="bk-child-count">Children *</label>
                <input id="bk-child-count" type="number" min="0" className="input-brutal" required disabled={readOnly} value={form.child_count} onChange={set("child_count")} data-testid="booking-child-count-input" />
              </div>
              <div>
                <label className="label-brutal" htmlFor="bk-child-price">Child rate (€) *</label>
                <input id="bk-child-price" type="number" min="0" step="0.01" className="input-brutal" required disabled={readOnly} value={form.child_price} onChange={set("child_price")} data-testid="booking-child-price-input" />
              </div>
              <div>
                <label className="label-brutal" htmlFor="bk-adult-count">Adults *</label>
                <input id="bk-adult-count" type="number" min="0" className="input-brutal" required disabled={readOnly} value={form.adult_count} onChange={set("adult_count")} data-testid="booking-adult-count-input" />
              </div>
              <div>
                <label className="label-brutal" htmlFor="bk-adult-price">Adult rate (€) *</label>
                <input id="bk-adult-price" type="number" min="0" step="0.01" className="input-brutal" required disabled={readOnly} value={form.adult_price} onChange={set("adult_price")} data-testid="booking-adult-price-input" />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="label-brutal" htmlFor="bk-attending">Number attending *</label>
                <input id="bk-attending" type="number" min="1" className="input-brutal" required disabled={readOnly} value={form.number_attending} onChange={set("number_attending")} data-testid="booking-attending-input" />
              </div>
              <div>
                <label className="label-brutal" htmlFor="bk-price">Price per person (€) *</label>
                <input id="bk-price" type="number" min="0" step="0.01" className="input-brutal" required disabled={readOnly} value={form.price_per_person} onChange={set("price_per_person")} data-testid="booking-price-input" />
              </div>
            </>
          )}
          <div>
            <label className="label-brutal" htmlFor="bk-aids">Skating aids to book ({euro(AID_PRICE)} each)</label>
            <select id="bk-aids" className="input-brutal" disabled={readOnly} value={form.skating_aids} onChange={set("skating_aids")} data-testid="booking-skating-aids-select">
              {Array.from({ length: 31 }, (_, i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label className="label-brutal" htmlFor="bk-free-aids">Free skating aids (with group)</label>
            <input id="bk-free-aids" type="number" min="0" className="input-brutal" disabled={readOnly} value={form.free_aids} onChange={set("free_aids")} data-testid="booking-free-aids-input" />
          </div>
          <div>
            <label className="label-brutal" htmlFor="bk-vat">VAT rate</label>
            <select id="bk-vat" className="input-brutal" disabled={readOnly} value={form.vat_rate} onChange={set("vat_rate")} data-testid="booking-vat-rate-select">
              <option value={0}>No VAT</option>
              <option value={9}>9%</option>
              <option value={13.5}>13.5%</option>
              <option value={23}>23%</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <div className="border-2 border-slate-900 bg-amber-300 p-3 shadow-[2px_2px_0px_0px_#0f172a] flex items-center justify-between flex-wrap gap-2" data-testid="booking-total-display">
              <span className="font-mono-tight text-xs uppercase tracking-widest font-bold">
                {form.split_pricing
                  ? `Children ${euro(childTotal)} + Adults ${euro(adultTotal)}`
                  : `Skaters ${euro(skatersTotal)}`}{aidsTotal > 0 ? ` + Aids ${euro(aidsTotal)}` : ""}
              </span>
              <span className="font-display text-2xl text-right">
                {vatRate > 0 && (
                  <span className="block text-[11px] font-mono-tight tracking-widest uppercase">
                    Total {euro(total)} + VAT {vatRate}% {euro(vatAmount)}
                  </span>
                )}
                {vatRate > 0 ? `Overall ${euro(grandTotal)}` : `Total ${euro(total)}`}
              </span>
            </div>
          </div>
          <Section title="Payment & ticketsolve" testId="section-payment" />
          <div>
            <label className="label-brutal" htmlFor="bk-payment">Payment status</label>
            <select id="bk-payment" className="input-brutal" disabled={readOnly} value={form.payment_status} onChange={set("payment_status")} data-testid="booking-payment-select">
              <option value="awaiting">Awaiting confirmation</option>
              <option value="confirmed">Booking confirmation</option>
              <option value="invoiced">Invoiced</option>
              <option value="invoiced_reminder">Invoiced — reminder sent</option>
              <option value="transfer">Paid — Bank transfer</option>
              <option value="card">Paid — Card</option>
              <option value="cash">Paid — Cash</option>
              <option value="quotation">Quotation</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </div>
          <div>
            <label className="label-brutal" htmlFor="bk-ts">Allocation removed from Ticketsolve sales? *</label>
            <select id="bk-ts" className="input-brutal" required disabled={readOnly} value={form.allocation_removed} onChange={set("allocation_removed")} data-testid="booking-allocation-removed-select">
              <option value="" disabled>Select…</option>
              <option value="yes">Yes — removed from public allocation</option>
              <option value="no">No — still in public allocation</option>
              <option value="na">N/A</option>
            </select>
            {!readOnly && (
              <p className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500 mt-1.5">
                Go into Ticketsolve and take these attendees out of public allocation
              </p>
            )}
          </div>
          <div>
            <label className="label-brutal" htmlFor="bk-po">PO number (optional)</label>
            <input id="bk-po" className="input-brutal" disabled={readOnly} value={form.po_number} onChange={set("po_number")} placeholder="Purchase order reference" data-testid="booking-po-number-input" />
          </div>
          <Section title="Additional info & notes" testId="section-notes" />
          <div className="md:col-span-2">
            <label className="label-brutal" htmlFor="bk-info">Additional information</label>
            <textarea id="bk-info" rows={2} className="input-brutal resize-y" disabled={readOnly} value={form.additional_info} onChange={set("additional_info")} placeholder="Skate aids, wheelchair access, birthday cake…" data-testid="booking-additional-info-input" />
          </div>
          <div className="md:col-span-2">
            <label className="label-brutal" htmlFor="bk-notes">Notes (internal)</label>
            <textarea id="bk-notes" rows={2} className="input-brutal resize-y" disabled={readOnly} value={form.notes} onChange={set("notes")} placeholder="Office notes — deposit paid, called on…" data-testid="booking-notes-input" />
          </div>

          <div className="md:col-span-2 flex justify-end gap-3 mt-2">
            <button type="button" className="btn-secondary" onClick={() => onOpenChange(false)} data-testid="booking-cancel-button">
              {readOnly ? "Close" : "Cancel"}
            </button>
            {!readOnly && (
              <button type="submit" className="btn-primary" disabled={busy} data-testid="booking-submit-button">
                {busy ? "Saving…" : mode === "new" ? "Create booking" : "Save changes"}
              </button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
